import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  Typography,
  Drawer,
  Table,
  Input,
  Space,
  message,
  DatePicker,
  Badge,
  Modal,
} from "antd";
import { ExpenseServices } from "../../../../Services/Manage/ExpenseServices";
import { ServicetypeService } from "../../../../Services/Manage/ServicetypeService";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import CreateProEx from "./CreateProEx";
import ProExpensePay from "../../../modals/ProExpensePay";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ManageProEx = () => {
  const [originalData, setOriginalData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [vendorOption, setVendorOption] = useState([]);
  const navigate = useNavigate();
 const [projectPaidMap, setProjectPaidMap] = useState({});

  // Payment Modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectGrandTotal, setProjectGrandTotal] = useState(0);

  // Fetch All Expenses
  const getAllExpense = async () => {
    try {
      const res = await ExpenseServices.getAllExpense();
      const data = res.data || [];
      // Filter only project-type expenses
      const projectExpenses = data.filter(item => item.type === "project");
      setOriginalData(projectExpenses);
    } catch (error) {
      console.error(error);
      messageApi.error("Failed to fetch project expenses");
    }
  };

  // Fetch Vendors (for display)
  const GetAllVendors = async () => {
    try {
      const res = await ServicetypeService.getAllVendors();
      setVendorOption(
        res.data.map(item => ({ label: item.name, value: item.id }))
      );
    } catch (error) {
      console.error(error);
    }
  };
  

  useEffect(() => {
    getAllExpense();
    GetAllVendors();
  }, []);

  // Filter Projects by Search & Date
  const filteredProjects = useMemo(() => {
    const [start, end] = dateRange;
    const lowerSearch = searchText.toLowerCase();

    return originalData
      .map(group => {
        const project = group.project;
        const matchesSearch =
          project?.pro_name?.toLowerCase().includes(lowerSearch) ||
          project?.pro_no?.toLowerCase().includes(lowerSearch) ||
          project?.pro_description?.toLowerCase().includes(lowerSearch);

        const filteredItems = group.data.filter(exp => {
          const vendorName = vendorOption.find(v => v.value === exp.vendor_id)?.label || "";
          const desc = (exp.description || "").toLowerCase();
          const itemMatchesSearch = desc.includes(lowerSearch) || vendorName.toLowerCase().includes(lowerSearch);

          const addedOn = exp.added_on ? new Date(exp.added_on) : null;
          const matchesDate = !start || !end || !addedOn || (addedOn >= start && addedOn <= end);

          return itemMatchesSearch && matchesDate;
        });

        if (matchesSearch || filteredItems.length > 0) {
          return { ...group, data: filteredItems };
        }
        return null;
      })
      .filter(Boolean);
  }, [originalData, searchText, dateRange, vendorOption]);

  // Calculate Totals for a Project
  const calculateProjectTotals = (projectGroup) => {
    const items = projectGroup.data || [];

    const grandTotal = items.reduce((sum, item) => {
      const price = Number(item.totalprice) || 0;
      const taxPercent = Number(item.tax) || 0;
      const taxAmount = (price * taxPercent) / 100;
      return sum + price + taxAmount;
    }, 0);

    const paidAmount = items.reduce((sum, item) => {
      if (Array.isArray(item.payments)) {
        return sum + item.payments.reduce((pSum, p) => pSum + Number(p.paid_amt || 0), 0);
      }
      return sum;
    }, 0);

    const pendingAmount = grandTotal - paidAmount;

    let status = "Unpaid";
    if (paidAmount >= grandTotal && grandTotal > 0) status = "Paid";
    else if (paidAmount > 0) status = "Partially Paid";

    return { grandTotal, paidAmount, pendingAmount, status };
  };

  // Pay Now Handler
const handlePayNow = (record) => {
  const { grandTotal } = calculateProjectTotals(record);

  setProjectGrandTotal(grandTotal);
  setSelectedProject(record);
  setPaymentModalOpen(true);
};



  const projectColumns = [
    {
      title: "S.No",
      key: "sno",
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Project Name",
      dataIndex: ["project", "pro_name"],
      render: text => <strong>{text || "-"}</strong>,
    },
    {
      title: "Project No.",
      dataIndex: ["project", "pro_no"],
      render: text => <strong>{text || "-"}</strong>,
    },
    {
      title: "Items",
      align: "center",
      render: (_, r) => r.data?.length || 0,
    },
    {
      title: "Grand Total",
      align: "right",
      render: (_, record) => {
        const { grandTotal } = calculateProjectTotals(record);
        return <strong>{grandTotal.toFixed(2)}</strong>;
      },
    },
  {
  title: "Paid",
  align: "right",
  render: (_, record) => {
const paid =
  projectPaidMap[record.project.pro_no] !== undefined
    ? projectPaidMap[record.project.pro_no]
    : calculateProjectTotals(record).paidAmount;



    return (
      <span style={{ color: "#52c41a" }}>
        {paid.toFixed(2)}
      </span>
    );
  },
},

    // {
    //   title: "Pending",
    //   align: "right",
    //   render: (_, record) => {
    //     const { pendingAmount } = calculateProjectTotals(record);
    //     return (
    //       <span style={{ color: pendingAmount > 0 ? "#fa541c" : "#52c41a" }}>
    //         {pendingAmount.toFixed(2)}
    //       </span>
    //     );
    //   },
    // },
    {
      title: "Status",
      align: "center",
      render: (_, record) => {
        const { status } = calculateProjectTotals(record);
        const color = status === "Paid" ? "success" : status === "Partially Paid" ? "warning" : "error";
        return <Badge status={color} text={status} />;
      },
    },
    {
      title: "Action",
      align: "center",
      render: (_, record) => (
        <Button type="primary" onClick={() => handlePayNow(record)}>
          Pay Now
        </Button>
      ),
    },
  ];

  // Sub-table columns (inside expandable row)
  const subColumns = [
    { title: "S.No", render: (_, __, i) => i + 1, width: 60 },
    { title: "Description", dataIndex: "description" },
    { title: "Qty", dataIndex: "qty", width: 80 },
    { title: "Unit Price", dataIndex: "unitprice", width: 100 },
    { title: "Total Price", dataIndex: "totalprice", width: 100 },
    { title: "Tax (%)", dataIndex: "tax", width: 80 },
    {
      title: "Tax Amount",
      render: (_, r) => {
        const price = Number(r.totalprice) || 0;
        const tax = Number(r.tax) || 0;
        return ((price * tax) / 100).toFixed(2);
      },
    },
    {
      title: "Final Amount",
      render: (_, r) => {
        const price = Number(r.totalprice) || 0;
        const tax = Number(r.tax) || 0;
        return (price + (price * tax) / 100).toFixed(2);
      },
    },
  ];

  const handleClearFilters = () => {
    setSearchText("");
    setDateRange([null, null]);
  };

  return (
    <>
      {contextHolder}

      {/* Payment Modal */}
      <Modal
        title={<Title level={4}>Pay Project Expense - {selectedProject?.project?.pro_no}</Title>}
        open={paymentModalOpen}
        onCancel={() => setPaymentModalOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        {selectedProject && (
    <ProExpensePay
  quotationNo={selectedProject?.project?.pro_no}
  grandTotal={projectGrandTotal}
  onPaidChange={(paid) => {
    setProjectPaidMap(prev => ({
      ...prev,
      [selectedProject.project.pro_no]: paid,
    }));
  }}
/>

        )}
      </Modal>

      <Card
        title={<Title level={4}>Manage Project Expenses</Title>}
        extra={
          <Space>
            <Button size="large" onClick={() => navigate("/projects/create")}>
              Create Project
            </Button>
            <Button type="primary" size="large" onClick={() => setDrawerOpen(true)}>
              Add Project Expense
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="Search Project Name, No., Description..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 400 }}
              size="large"
              allowClear
            />
            <RangePicker
              size="large"
              format="DD MMM YYYY"
              onChange={(dates) => {
                if (dates) {
                  setDateRange([
                    dates[0].startOf("day").toDate(),
                    dates[1].endOf("day").toDate(),
                  ]);
                } else {
                  setDateRange([null, null]);
                }
              }}
            />
            <Button size="large" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Space>
        </Card>

        {/* Project Expenses Table */}
        <Table
          rowKey={(r) => r?.project?.id}
          dataSource={filteredProjects}
          columns={projectColumns}
          pagination={{ pageSize: 10 }}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                dataSource={record.data}
                columns={subColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ),
          }}
        />
      </Card>

      {/* Add/Edit Drawer */}
      <Drawer
        title="Add Project Expense"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={1200}
      >
        <CreateProEx onClose={() => setDrawerOpen(false)} getAllExpense={getAllExpense} />
      </Drawer>
    </>
  );
};

export default ManageProEx;